import { CssBaseline, ThemeProvider } from '@mui/material';
import { Provider as ReduxProvider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import RequireAuth from './components/RequireAuth';
import { AdSenseScript } from './lib/adsense/adsense-script';
import { brandingFixture } from './lib/branding/branding-fixture';
import AboutPage from './pages/AboutPage';
import DailyPage from './pages/DailyPage';
import DayDetailPage from './pages/DayDetailPage';
import DaysPage from './pages/DaysPage';
import GamePage from './pages/GamePage';
import GamesPage from './pages/GamesPage';
import NewsletterPage from './pages/NewsletterPage';
import NotFoundPage from './pages/NotFoundPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import PodcastPage from './pages/PodcastPage';
import ProfilePage from './pages/ProfilePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AdminLandingPage from './pages/AdminLandingPage';
import AdminPendingPage from './pages/AdminPendingPage';
import AdminSubmitPage from './pages/AdminSubmitPage';
import { RequireAdmin } from './components/RequireAdmin';
import { store } from './store';
import { createAppTheme } from './theme';

const theme = createAppTheme(brandingFixture);

/**
 * Root SPA component per public-website design §3.2.
 *
 * Provider stack (outer → inner):
 *
 *   <HelmetProvider>     — react-helmet-async for SEO meta injection (task 10.x)
 *     <ReduxProvider>    — Redux store (task 4.4)
 *       <ThemeProvider>  — MUI theme built from BrandingConfig v1 tokens (task 4.1)
 *         <CssBaseline>  — MUI baseline CSS reset
 *         <BrowserRouter>— react-router-dom v6
 *           <AppShell>   — chrome (header + drawer + footer; task 4.8)
 *             <Routes>   — content-bearing routes:
 *               /              → DailyPage (today, with Requirement 1.6 fallback)
 *               /daily/:date   → DayDetailPage (parameterized historical date)
 *               /days          → DaysPage (paginated reverse-chronological summaries)
 *               /games         → GamesPage (search + reverse-chronological grid)
 *               /games/:gameId → GamePage (full Game-page detail surface)
 *               /sign-in       → SignInPage (Hosted UI federated sign-in; task 7.6)
 *               /oauth-callback→ OAuthCallbackPage (PKCE token exchange; task 7.6)
 *               /sign-up       → <RequireAuth><SignUpPage></RequireAuth> (task 8.6)
 *               /profile       → <RequireAuth><ProfilePage></RequireAuth> (task 8.6)
 *               /newsletter    → NewsletterPage (NOT wrapped — page handles
 *                                unauthenticated branch inline per Req 5.9 / Req 7.2)
 *               /podcast       → PodcastPage (subscribe surfaces; Requirement 4)
 *               /about         → AboutPage (static About copy; Requirement 2.7)
 *               *              → NotFoundPage (catch-all 404; Requirement 3.6 —
 *                                declared last so the catch-all is the
 *                                visually obvious final fallback)
 *
 * Concrete routes for major task 5 (content live-hydration) wired here
 * per task 5.6. Major task 6 (games) added `/games` and `/games/:gameId`
 * per task 6.10. Major task 7 (auth) added `/sign-in` and
 * `/oauth-callback` per task 7.6 + 7.8. Sign-out has **no route** —
 * the SideNav's "Sign out" button calls `performSignOut` directly per
 * Requirement 5.8 + design §2.3. Major task 8 (profile + sign-up +
 * newsletter) added `/sign-up`, `/profile`, and `/newsletter` per
 * task 8.6: `/sign-up` and `/profile` are wrapped by `<RequireAuth>`
 * per Requirement 5.9; `/newsletter` is **not** wrapped — the page
 * itself handles the unauthenticated branch inline per Requirement
 * 7.2. Major task 9 (static pages) added `/podcast`, `/about`, and
 * the catch-all `*` per task 9.5: all three are unauthenticated
 * content surfaces — none are wrapped by `<RequireAuth>` per
 * Requirement 5.9 — and the catch-all `*` is declared last in the
 * `<Routes>` block as the visually obvious final fallback. Task 10
 * (SEO) wires `<SeoHead>` into each page's own component but adds
 * no new routes.
 *
 * The `<ThemeProvider>` wraps the entire tree (including `<BrowserRouter>`
 * and `<AppShell>`) so MUI components anywhere downstream resolve theme
 * tokens through one source of truth per Property 9.
 *
 * The branding tokens come from `lib/branding/branding-fixture.ts` — a
 * placeholder until the `@dvgup/branding` workspace package scaffolds
 * (TODO-013 DEFERRED). Migration to the real runtime export is a single-
 * import swap.
 */
export function App(): JSX.Element {
  return (
    <HelmetProvider>
      <ReduxProvider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AdSenseScript />
          <BrowserRouter>
            <AppShell>
              <Routes>
                <Route path="/" element={<DailyPage />} />
                <Route path="/daily/:date" element={<DayDetailPage />} />
                <Route path="/days" element={<DaysPage />} />
                <Route path="/games" element={<GamesPage />} />
                <Route path="/games/:gameId" element={<GamePage />} />
                {/*
                 * Authentication routes (task 7.6 + task 7.8). Sign-out
                 * is NOT a route — the SideNav's "Sign out" button
                 * (`src/components/SideNav.tsx`) calls `performSignOut`
                 * (`src/lib/auth/sign-out.ts`) directly per Requirement
                 * 5.8 + design §2.3.
                 */}
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/sign-up" element={<SignUpPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
                {/*
                 * Profile + sign-up surfaces (task 8.6). Both routes
                 * are wrapped by `<RequireAuth>` (`src/components/
                 * RequireAuth.tsx`, task 7.7) per Requirement 5.9 —
                 * unauthenticated visits redirect to
                 * `/sign-in?next=<encoded path>` so the OAuth
                 * round-trip can land them back on the originally-
                 * requested URL.
                 */}
                <Route
                  path="/profile"
                  element={
                    <RequireAuth>
                      <ProfilePage />
                    </RequireAuth>
                  }
                />
                {/*
                 * Admin surface (round-22 content-pipeline phase 3).
                 * `<RequireAdmin>` renders children only for
                 * authenticated members of the `admin` Cognito group;
                 * non-admins get redirected to `/`. The real security
                 * boundary is the server-side admin-group check
                 * inside every admin handler.
                 */}
                <Route
                  path="/admin"
                  element={
                    <RequireAdmin>
                      <AdminLandingPage />
                    </RequireAdmin>
                  }
                />
                <Route
                  path="/admin/pending"
                  element={
                    <RequireAdmin>
                      <AdminPendingPage />
                    </RequireAdmin>
                  }
                />
                <Route
                  path="/admin/submit"
                  element={
                    <RequireAdmin>
                      <AdminSubmitPage />
                    </RequireAdmin>
                  }
                />
                {/*
                 * Newsletter signup (task 8.6). NOT wrapped by
                 * `<RequireAuth>` — per Requirement 5.9 + Requirement
                 * 7.2, the page itself renders the "Sign in to
                 * subscribe" CTA inline for unauthenticated visitors
                 * rather than redirecting to `/sign-in`.
                 */}
                <Route path="/newsletter" element={<NewsletterPage />} />
                {/*
                 * Static content surfaces (task 9.5) — Requirement
                 * 4 (`/podcast`), Requirement 2.7 (`/about`), and
                 * Requirement 3.6 (catch-all 404). None are wrapped
                 * by `<RequireAuth>` per Requirement 5.9; all three
                 * render for unauthenticated visitors. The catch-
                 * all `*` is declared **last** — react-router v6
                 * scores routes by specificity (so the wildcard
                 * already loses to any concrete path-pattern), but
                 * keeping `*` at the bottom of this `<Routes>` block
                 * is the team convention so the catch-all is always
                 * visually obvious as the final fallback. New routes
                 * MUST be added **above** this comment.
                 */}
                <Route path="/podcast" element={<PodcastPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppShell>
          </BrowserRouter>
        </ThemeProvider>
      </ReduxProvider>
    </HelmetProvider>
  );
}
